/** Small bounded format readers shared by the API and the browser. Not a general image decoder. */
export interface ImageInfo { type: string; width: number; height: number }
const invalid = (): never => { throw new Error('Повреждённая или неподдерживаемая картинка'); };
export function inspectRaster(bytes: Uint8Array, maxDimension = 8192, maxPixels = 16_000_000): ImageInfo {
  if (bytes.length < 12) return invalid();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const word = (p: number, n: number) => String.fromCharCode(...bytes.subarray(p, p + n));
  let type = '', width = 0, height = 0;
  if (word(0, 8) === '\x89PNG\r\n\x1a\n') {
    type = 'image/png'; let ended = false, data = false, header = false;
    for (let p = 8; p + 12 <= bytes.length;) {
      const n = view.getUint32(p), name = word(p + 4, 4);
      if (n > bytes.length - p - 12) return invalid();
      if (!header) {
        if (name !== 'IHDR' || n !== 13) return invalid();
        width = view.getUint32(p + 8); height = view.getUint32(p + 12); header = true;
      } else if (name === 'IHDR' || name === 'acTL') return invalid();
      if (name === 'IDAT') data = true;
      p += n + 12;
      if (name === 'IEND') { if (n || p !== bytes.length) return invalid(); ended = true; break; }
    }
    if (!ended || !data) return invalid();
  } else if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    type = 'image/jpeg';
    if (bytes.at(-2) !== 0xff || bytes.at(-1) !== 0xd9) return invalid();
    for (let p = 2; p + 4 <= bytes.length;) {
      if (bytes[p++] !== 0xff) return invalid();
      while (bytes[p] === 0xff) p++;
      const marker = bytes[p++];
      if (marker === 0xda || marker === 0xd9) break;
      const n = view.getUint16(p);
      if (n < 2 || p + n > bytes.length) return invalid();
      if ([0xc0, 0xc1, 0xc2].includes(marker)) {
        if (n < 8) return invalid();
        height = view.getUint16(p + 3); width = view.getUint16(p + 5);
      }
      p += n;
    }
  } else if (word(0, 4) === 'RIFF' && word(8, 4) === 'WEBP') {
    type = 'image/webp';
    if (view.getUint32(4, true) + 8 !== bytes.length) return invalid();
    const sizes: [number, number][] = [];
    const u24 = (p: number) => bytes[p] | (bytes[p + 1] << 8) | (bytes[p + 2] << 16);
    let pixels = false;
    for (let p = 12; p + 8 <= bytes.length;) {
      const name = word(p, 4), n = view.getUint32(p + 4, true), d = p + 8;
      if (d + n > bytes.length) return invalid();
      if (name === 'ANIM' || name === 'ANMF') return invalid();
      if (name === 'VP8X') {
        if (n !== 10 || (bytes[d] & 2)) return invalid();
        sizes.push([u24(d + 4) + 1, u24(d + 7) + 1]);
      } else if (name === 'VP8 ') {
        if (n < 10 || word(d + 3, 3) !== '\x9d\x01\x2a') return invalid();
        sizes.push([view.getUint16(d + 6, true) & 0x3fff, view.getUint16(d + 8, true) & 0x3fff]); pixels = true;
      } else if (name === 'VP8L') {
        if (n < 5 || bytes[d] !== 0x2f) return invalid();
        sizes.push([1 + (bytes[d + 1] | ((bytes[d + 2] & 63) << 8)),
          1 + ((bytes[d + 2] >> 6) | (bytes[d + 3] << 2) | ((bytes[d + 4] & 15) << 10))]); pixels = true;
      }
      p = d + n + (n & 1);
      if (p > bytes.length) return invalid();
    }
    if (!pixels || !sizes.length || sizes.some(([w, h]) => w !== sizes[0][0] || h !== sizes[0][1])) return invalid();
    [width, height] = sizes[0];
  } else if (word(0, 6) === 'GIF87a' || word(0, 6) === 'GIF89a') {
    type = 'image/gif'; width = view.getUint16(6, true); height = view.getUint16(8, true);
    if (bytes.length < 14) return invalid();
    let p = 13 + ((bytes[10] & 128) ? 3 * 2 ** ((bytes[10] & 7) + 1) : 0), frames = 0, ended = false;
    const skip = () => { while (p < bytes.length) { const n = bytes[p++]; if (!n) return; p += n; } return invalid(); };
    while (p < bytes.length) {
      const block = bytes[p++];
      if (block === 0x3b) { ended = p === bytes.length; break; }
      if (block === 0x21) { p++; skip(); }
      else if (block === 0x2c) {
        if (p + 9 > bytes.length || ++frames > 1) return invalid();
        const w = view.getUint16(p + 4, true), h = view.getUint16(p + 6, true);
        if (!w || !h || w > width || h > height) return invalid();
        const packed = bytes[p + 8]; p += 9 + ((packed & 128) ? 3 * 2 ** ((packed & 7) + 1) : 0);
        p++; skip();
      } else return invalid();
    }
    if (!ended || frames !== 1) return invalid();
  } else return invalid();
  if (!width || !height || width > maxDimension || height > maxDimension || width * height > maxPixels) {
    throw new Error('Картинка слишком большая');
  }
  return { type, width, height };
}
export function checkSvg(text: string): void {
  // Only bounded, static, self-contained vector icons. They must STILL be used in <img>, never HTML.
  if (text.length > 256 * 1024 || !/<svg(?:\s|>)/i.test(text) || (text.match(/</g)?.length ?? 0) > 3000
    || /<!DOCTYPE|<!ENTITY|<\s*(?:[a-z_][\w.-]*:)?(?:script|foreignObject|image|feImage|filter|animate\w*|set|style)\b|\bon[a-z]+\s*=|\bstyle\s*=|@import|&(?:#|[a-z])/i.test(text)
    || /(?:href|src)\s*=\s*["']\s*(?!#)/i.test(text)
    || /url\(\s*["']?\s*(?!#)/i.test(text)) throw new Error('SVG должен быть статическим и не содержать внешних ресурсов');
  for (const m of text.matchAll(/\b(?:width|height)\s*=\s*["']([^"']+)["']/gi)) {
    if (/^\d+(?:\.\d+)?%$/.test(m[1])) continue;
    const value = Number(m[1].replace(/px$/, ''));
    if (!Number.isFinite(value) || value <= 0 || value > 8192) throw new Error('Размеры SVG недопустимы');
  }
}
export function validateThumbnail(value: string): boolean {
  try {
    const m = /^data:(image\/(?:png|jpeg|webp));base64,([A-Za-z0-9+/]+={0,2})$/.exec(value);
    if (!m || value.length > 120_000 || m[2].length % 4) return false;
    const raw = atob(m[2]);
    if (btoa(raw) !== m[2]) return false;
    return inspectRaster(Uint8Array.from(raw, (c) => c.charCodeAt(0)), 96, 96 * 96).type === m[1];
  } catch { return false; }
}
